<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

import { useAccount } from 'dashboard/composables/useAccount';
import Icon from 'next/icon/Icon.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ChannelItem from 'dashboard/components/widgets/ChannelItem.vue';
import { isApiInboxDisabledForAccount } from './apiInboxVisibility';

const { t } = useI18n();
const router = useRouter();
const { accountId, currentAccount } = useAccount();

const enabledFeatures = computed(() => currentAccount.value?.features || {});

const channelList = computed(() => {
  const channels = [
    {
      key: 'whatsapp',
      title: t('INBOX_MGMT.ADD.AUTH.CHANNEL.WHATSAPP.TITLE'),
      description: t('INBOX_MGMT.ADD.AUTH.INTRO.WHATSAPP_DESCRIPTION'),
      badge: t('INBOX_MGMT.ADD.AUTH.INTRO.WHATSAPP_BADGE'),
      highlights: [
        t('INBOX_MGMT.ADD.AUTH.INTRO.WHATSAPP_CLOUD'),
        t('INBOX_MGMT.ADD.AUTH.INTRO.WHATSAPP_COEXISTENCE'),
      ],
      icon: 'i-brand-whatsapp',
      featured: true,
    },
    {
      key: 'facebook',
      title: t('INBOX_MGMT.ADD.AUTH.CHANNEL.FACEBOOK.TITLE'),
      description: t('INBOX_MGMT.ADD.AUTH.CHANNEL.FACEBOOK.DESCRIPTION'),
      icon: 'i-brand-facebook',
    },
    {
      key: 'instagram',
      title: t('INBOX_MGMT.ADD.AUTH.CHANNEL.INSTAGRAM.TITLE'),
      description: t('INBOX_MGMT.ADD.AUTH.CHANNEL.INSTAGRAM.DESCRIPTION'),
      icon: 'i-brand-instagram',
    },
    {
      key: 'telegram',
      title: t('INBOX_MGMT.ADD.AUTH.CHANNEL.TELEGRAM.TITLE'),
      description: t('INBOX_MGMT.ADD.AUTH.CHANNEL.TELEGRAM.DESCRIPTION'),
      icon: 'i-brand-telegram',
    },
    {
      key: 'email',
      title: t('INBOX_MGMT.ADD.AUTH.CHANNEL.EMAIL.TITLE'),
      description: t('INBOX_MGMT.ADD.AUTH.CHANNEL.EMAIL.DESCRIPTION'),
      icon: 'i-hugeicons-mail-01',
    },
    {
      key: 'api',
      title: t('INBOX_MGMT.ADD.AUTH.CHANNEL.API.TITLE'),
      description: t('INBOX_MGMT.ADD.AUTH.CHANNEL.API.DESCRIPTION'),
      icon: 'i-lucide-braces',
    },
    {
      key: 'website',
      title: t('INBOX_MGMT.ADD.AUTH.CHANNEL.WEBSITE.TITLE'),
      description: t('INBOX_MGMT.ADD.AUTH.CHANNEL.WEBSITE.DESCRIPTION'),
      icon: 'i-lucide-panels-top-left',
    },
  ];

  if (isApiInboxDisabledForAccount(accountId.value)) {
    return channels.filter(channel => channel.key !== 'api');
  }

  return channels;
});

const featuredChannel = computed(() =>
  channelList.value.find(channel => channel.featured)
);

const otherChannels = computed(() =>
  channelList.value.filter(channel => !channel.featured)
);

const goToInboxList = () => {
  router.push({
    name: 'settings_inbox_list',
    params: { accountId: accountId.value },
  });
};

const initChannelAuth = channel => {
  router.push({
    name: 'settings_inboxes_page_channel',
    params: {
      sub_page: channel,
      accountId: accountId.value,
    },
  });
};
</script>

<template>
  <div class="flex flex-col gap-6 p-5 sm:p-7">
    <section
      class="flex flex-col gap-4 border-b border-n-slate-5 pb-6 dark:border-n-slate-7 sm:flex-row sm:items-center"
    >
      <div
        class="flex size-10 shrink-0 items-center justify-center rounded-xl bg-n-alpha-2 text-n-brand"
      >
        <Icon icon="i-lucide-circle-plus" class="size-5" />
      </div>

      <div class="min-w-0 flex-1">
        <span
          class="text-xs font-semibold uppercase tracking-wide text-n-brand"
        >
          {{ $t('INBOX_MGMT.ADD.AUTH.INTRO.EYEBROW') }}
        </span>
        <h1 class="mt-1 text-xl font-semibold tracking-tight text-n-slate-12">
          {{ $t('INBOX_MGMT.ADD.AUTH.INTRO.TITLE') }}
        </h1>
        <p class="mt-1 text-sm leading-5 text-n-slate-10">
          {{ $t('INBOX_MGMT.ADD.AUTH.INTRO.DESCRIPTION') }}
        </p>
      </div>

      <Button
        :label="$t('INBOX_MGMT.ADD.AUTH.INTRO.BACK_TO_CONNECTIONS')"
        icon="i-lucide-arrow-left"
        variant="outline"
        color="slate"
        size="sm"
        class="shrink-0"
        @click="goToInboxList"
      />
    </section>

    <section v-if="featuredChannel">
      <ChannelItem
        :channel="featuredChannel"
        :enabled-features="enabledFeatures"
        @channel-item-click="initChannelAuth"
      />
    </section>

    <section>
      <div class="mb-4 flex items-end justify-between gap-4">
        <div>
          <h2 class="text-base font-semibold text-n-slate-12">
            {{ $t('INBOX_MGMT.ADD.AUTH.INTRO.OTHER_TITLE') }}
          </h2>
          <p class="mt-1 text-sm text-n-slate-10">
            {{ $t('INBOX_MGMT.ADD.AUTH.INTRO.OTHER_DESCRIPTION') }}
          </p>
        </div>
        <span
          class="hidden rounded-full bg-n-alpha-2 px-2.5 py-1 text-xs font-medium text-n-slate-10 sm:inline-flex"
        >
          {{ otherChannels.length }}
        </span>
      </div>

      <div class="grid grid-cols-1 gap-3.5 sm:grid-cols-2 xl:grid-cols-3">
        <ChannelItem
          v-for="channel in otherChannels"
          :key="channel.key"
          :channel="channel"
          :enabled-features="enabledFeatures"
          @channel-item-click="initChannelAuth"
        />
      </div>
    </section>
  </div>
</template>
